import posthog from 'posthog-js';

import { testLoginConfig } from './config';
import { createTestUser, type TestIdentity } from './createTestUser';
import { identifyPostHogUser } from '@/auth/identifyPostHogUser';
import { syncAuthCookie, userManager } from '@/auth/userManager';
import { useLocaleStore } from '@/stores/locale';

/**
 * Stores a freshly-minted test user so the rest of the app sees an
 * authenticated session, and writes the legacy auth cookies. `storeUser` does
 * not emit `addUserLoaded` (and there's no signin callback in the test flow),
 * so we call `syncAuthCookie` directly — otherwise the cookie-authenticated
 * `/tlp` backend gets no credentials. The caller reloads afterwards so
 * `react-oidc-context` picks the user up from the OIDC store on mount.
 *
 * This is the test-flow analog of `App.tsx`'s OIDC `authSigninCallback`, so it
 * also identifies the user in PostHog — otherwise no signin callback ever fires
 * and feature flags resolve against an anonymous `distinct_id`. PostHog persists
 * the identity, so it survives the reload.
 */
export const applyTestUser = async (identity: TestIdentity): Promise<void> => {
  const user = await createTestUser(identity, testLoginConfig);

  await userManager.storeUser(user);
  syncAuthCookie(user.access_token);
  identifyPostHogUser(posthog, user, useLocaleStore.getState().lang);
};
